import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import get from "lodash/get";
import { AccessType } from "@quintype/components";
import { HeaderSection } from "./header-section/header-section";
import { PlanSection } from "./plan-section/plan-section";
import { WhyChoose } from "./why-choose/why-choose";
import { TestimonialsSection } from "./testimonials-section/testimonials-section";
import { FaqSection } from "./faq-section/faq-section";
import { FooterSection } from "./footer-section/footer-section";
import { setIsLogin, setUserDetails } from "../../store/actions";
import { createSubscriptionSession } from "./auth-client";
import { fetchCampaignData, getQueryParam, normalizeSubscriptionCampaign } from "./utils";
import "./subscription-page.m.css";
import { UserSnackbar } from "./user-snackbar/user-snackbar";

import { StatusModal } from "./status-modal/status-modal";

const SubscriptionPageInner = ({ campaign, getSubscription, initAccessType, getSubscriptionForUser, validateCoupon }) => {
  const [plans, setPlans] = useState([]);
  const [showSnackbar, setShowSnackbar] = useState(true);
  const [modalData, setModalData] = useState(null);
  const { isLogin, user, qt } = useSelector(s => ({ isLogin: s.isLogin, user: s.user?.user?.userData || s.user?.userData || s.user, qt: s.qt }));

  const handleLogout = () => {
    const sso = get(qt, ["config", "publisher-attributes", "sso_endpoint"]);
    if (sso) return window.location.href = `${sso}/auth/logout?redirectUrl=${encodeURIComponent(window.location.href)}`;
    ["access_token", "refresh_token", "user"].forEach(k => localStorage.removeItem(k));
    window.location.reload();
  };

  useEffect(() => {
    const exp = campaign?.["expiry-date"], red = campaign?.["expiry-redirection"] || campaign?.["redirection-url"], cc = qt?.countryCode || get(qt, ["config", "countryCode"]);
    if (cc && cc !== "IN" && red) return window.location.replace(red);
    if (exp && red) {
      const e = new Date(exp); e.setHours(23, 59, 59, 999);
      if (new Date() > e) setModalData({ type: "campaign_expired", redirectUrl: red });
    }
  }, [campaign, qt]);

  useEffect(() => {
    const cid = getQueryParam("id", window?.location?.search);
    if (!cid || !campaign) return;
    getSubscription().then(gs => {
      const { planProps } = normalizeSubscriptionCampaign(campaign, gs, cid);
      setPlans(planProps.plans);
    }).catch(() => { });
  }, [campaign]);

  useEffect(() => {
    const utm = campaign?.["utm-parameter"];
    if (typeof window !== "undefined" && utm && !window.location.search.includes(utm.split("=")[0])) {
      const search = window.location.search;
      const nextSearch = search ? (search.includes("?") ? `${search}&${utm}` : `${search}?${utm}`) : `?${utm}`;
      window.history.replaceState(null, "", window.location.pathname + nextSearch + window.location.hash);
    }
  }, [campaign]);

  const { heroSection, planProps, whyChooseProps } = normalizeSubscriptionCampaign(campaign);
  const expDate = campaign?.["expiry-date"], isCampaignExpired = expDate && new Date() > new Date(expDate).setHours(23, 59, 59, 999);

  if (isCampaignExpired) return <div styleName="subscription-page"><main styleName="subscription-content"><div style={{ background: "#FFF", padding: "40px 20px", borderRadius: 8, textAlign: "center" }}><h3>Campaign Expired</h3><p>Redirecting you to our plans...</p></div></main>{modalData && <StatusModal {...modalData} onClose={() => setModalData(null)} />}</div>;

  return (
    <>
      <div styleName="subscription-page" ref={useRef(null)}>
        <div styleName="background-layer" aria-hidden="true">
          <picture>
            <source media="(max-width: 767px)" srcSet={heroSection.background_mobile || heroSection.background_desktop} />
            <img src={heroSection.background_desktop || heroSection.background_mobile} alt="Background" />
          </picture>
        </div>
        <main styleName="subscription-content">
          <HeaderSection theme={heroSection} />
          <PlanSection {...planProps} plans={plans.length ? plans : planProps.plans} campaign={campaign} initAccessType={initAccessType} getSubscriptionForUser={getSubscriptionForUser} validateCoupon={validateCoupon} />
          <WhyChoose {...whyChooseProps} planCard={planProps.planCard} />
          <TestimonialsSection {...get(campaign, ["testimonial_section"], {})} colors={planProps.colors} fetchUrl="/pv-testimonial" />
          <FaqSection {...get(campaign, ["faq_section"], {})} colors={planProps.colors} fetchUrl="/pv-faq" />
          <FooterSection {...get(campaign, ["footer_section"], {})} colors={planProps.colors} />
        </main>
      </div>
      {isLogin && user && showSnackbar && <UserSnackbar user={user} onLogout={handleLogout} onClose={() => setShowSnackbar(false)} />}
      {modalData && <StatusModal {...modalData} onClose={() => setModalData(null)} />}
    </>
  );
};

const SubscriptionPage = () => {
  const dispatch = useDispatch();
  const [campaign, setCampaign] = useState(null);
  const [error, setError] = useState(null);
  const { qt, isLogin, user } = useSelector(s => {
    const u = s.user?.user?.userData || s.user?.userData || s.user, hasU = !!(u?.id || u?.email || u?.name);
    return { qt: s.qt, isLogin: s.isLogin || hasU, user: hasU ? u : null };
  });

  useEffect(() => { if (user && !isLogin) dispatch(setIsLogin(true)); }, [!!user, isLogin]);

  useEffect(() => {
    const cid = getQueryParam("id", window?.location?.search);
    if (!cid) return setError("Missing campaign");
    const ctrl = new AbortController();
    fetchCampaignData(cid, ctrl.signal).then(p => setCampaign(p)).catch(e => {
      if (e.name !== "AbortError") window.location.replace("/404");
    });
    return () => ctrl.abort();
  }, []);



  useEffect(() => {
    const search = window?.location?.search;
    if (!search?.includes("access_token") || isLogin) return;
    const raw = (search || "").replace(/\?/g, "&").replace("&", "?");
    const params = new URLSearchParams(raw), at = params.get("access_token"), rt = params.get("refresh_token");
    if (at && rt) createSubscriptionSession({ accessToken: at, refreshToken: rt }).then(u => {
      dispatch(setIsLogin(true)); dispatch(setUserDetails(u));
      params.delete("access_token"); params.delete("refresh_token");
      window.history.replaceState(null, "", window.location.pathname + (params.toString() ? `?${params.toString()}` : "") + window.location.hash);
    }).catch(() => { });
  }, [isLogin]);

  if (error || !campaign) return <div styleName="subscription-page"><main styleName="subscription-content"><div style={{ background: "#FFF", padding: 20, borderRadius: 8 }}>{error || "Loading..."}</div></main></div>;

  const ck = get(qt, ["config", "publisher-attributes", "subscription_setup"], {}), aKey = ck.accesstype_key || get(qt, ["config", "publisher-attributes", "accesstype_key"], "");
  return (
    <AccessType
      accessTypeKey={aKey}
      accessTypeBkIntegrationId={ck.accesstype_subauth || ""}
      enableAccesstype={true}
      email={user?.email || (user?.emails && user?.emails[0]) || ""}
      phone={user?.mobile || (user?.phoneNumbers && user?.phoneNumbers[0]) || ""}
    >
      {props => <SubscriptionPageInner campaign={campaign} {...props} />}
    </AccessType>
  );
};

export { SubscriptionPage };
